import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Download, Loader2 } from "lucide-react";

export default function Sidebar({ comuni, selected, onSelect, onChanged, counts, history, jobs = {} }) {
  const hist = history || {};
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }); } catch { return ""; } };
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [comuneToDelete, setComuneToDelete] = useState(null);
  const totalAziende = Object.values(counts || {}).reduce((a, b) => a + (b || 0), 0);

  const add = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await StatusApi.addComune(newName.trim());
      setNewName("");
      toast.success("Comune aggiunto");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    try {
      await StatusApi.deleteComune(id);
      toast.success("Comune rimosso");
      onChanged && onChanged();
    } catch {
      toast.error("Errore");
    }
  };

  const seed = async () => {
    try {
      const r = await StatusApi.seedComuni();
      toast.success(`${r.added} comuni precaricati`);
      onChanged && onChanged();
    } catch {
      toast.error("Errore nel precaricamento");
    }
  };

  return (
    <aside className="w-72 shrink-0 border-r border-border bg-white flex flex-col h-full" data-testid="sidebar">
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="h-4 w-4 text-primary" />
          <h2 className="font-display font-extrabold tracking-tight text-sm uppercase">Comuni</h2>
          <span className="ml-auto text-xs text-muted-foreground" data-testid="comuni-count">{comuni.length}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3" data-testid="total-aziende">
          {totalAziende.toLocaleString("it-IT")} aziende censite
        </p>
        <div className="flex gap-1.5">
          <Input
            data-testid="add-comune-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Nuovo comune..."
            className="h-9 text-sm"
          />
          <Button size="icon" onClick={add} disabled={adding} data-testid="add-comune-btn"
            className="h-9 w-9 shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {comuni.length === 0 && (
          <Button variant="outline" size="sm" onClick={seed} data-testid="seed-comuni-btn"
            className="w-full mt-2 text-xs">
            <Download className="h-3 w-3 mr-1" /> Precarica lista comuni
          </Button>
        )}
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2">
          {comuni.map((c) => {
            const active = selected === c.name;
            return (
              <div
                key={c.id}
                data-testid={`comune-item-${c.name}`}
                onClick={() => onSelect(c.name)}
                className={`group flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors duration-200 ${
                  active ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"
                }`}
              >
                {c.stemma_url ? (
                  <img src={c.stemma_url} alt="" loading="lazy"
                    data-testid={`comune-stemma-${c.name}`}
                    className="h-6 w-6 object-contain shrink-0 rounded-sm bg-white"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <MapPin className={`h-4 w-4 shrink-0 ${active ? "text-primary-foreground/80" : "text-muted-foreground"}`} />
                )}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium truncate block">{c.name}</span>
                  {jobs[c.name]?.status === "running" ? (
                    <span className={`text-[10px] truncate flex items-center gap-1 ${active ? "text-primary-foreground/90" : "text-primary"}`}
                      data-testid={`comune-running-${c.name}`}>
                      <Loader2 className="h-2.5 w-2.5 animate-spin shrink-0" /> Raccolta in corso…
                    </span>
                  ) : hist[c.name] && (
                    <span className={`text-[10px] truncate block ${active ? "text-primary-foreground/75" : "text-muted-foreground"}`}
                      data-testid={`comune-hist-${c.name}`}>
                      {hist[c.name].source} · {fmtDate(hist[c.name].at)}
                    </span>
                  )}
                </div>
                {counts[c.name] > 0 && (
                  <span className={`text-[11px] font-mono-plex px-1.5 py-0.5 rounded ${
                    active ? "bg-white/20" : "bg-muted-foreground/10 text-muted-foreground"
                  }`}>
                    {counts[c.name]}
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setComuneToDelete(c); }}
                  data-testid={`delete-comune-${c.name}`}
                  className={`opacity-0 group-hover:opacity-100 transition-opacity ${
                    active ? "text-white hover:text-red-200" : "text-muted-foreground hover:text-red-500"
                  }`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      <AlertDialog open={!!comuneToDelete} onOpenChange={(o) => !o && setComuneToDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere il comune {comuneToDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {counts[comuneToDelete?.name] > 0
                ? `Attenzione: questo comune contiene ${counts[comuneToDelete?.name]} nominativi. Rimuovendolo perderai il lavoro raccolto per ${comuneToDelete?.name}.`
                : `Stai per rimuovere ${comuneToDelete?.name} dalla lista.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-comune">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-comune"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { const id = comuneToDelete.id; setComuneToDelete(null); remove(id); }}>
              Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}
